import { useEffect, useRef } from "react";

/**
 * Animated clinical-tech background — pulsing nodes, connection lines, drifting glyphs.
 * Inspired by precision-lab control panels.
 *
 * Props:
 *   variant : "dark" (default, navy) | "light" (subtle on white) | "subtle" (for in-content)
 *   density : 0.5 .. 2 (default 1) — multiplier on node + glyph count
 *   showGrid : boolean — render the subway grid lines
 *   showScan : boolean — render the horizontal scan-lines sweep
 *   className : extra wrapper classes
 *
 * Honours prefers-reduced-motion.
 */
export default function TechBackground({
    variant = "dark",
    density = 1,
    showGrid = true,
    showScan = true,
    className = "",
}) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const cvs = canvasRef.current;
        if (!cvs) return;
        const ctx = cvs.getContext("2d");
        let raf = 0;
        let w = 0, h = 0, dpr = 1;
        let nodes = [];

        const palette = variant === "light"
            ? { node: "rgba(8, 145, 178, 0.55)", line: "rgba(8, 145, 178, 0.18)" }
            : variant === "subtle"
                ? { node: "rgba(8, 145, 178, 0.40)", line: "rgba(8, 145, 178, 0.10)" }
                : { node: "rgba(103, 232, 249, 0.70)", line: "rgba(103, 232, 249, 0.22)" };

        const NODE_COUNT = Math.round(28 * density);

        const resize = () => {
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = cvs.clientWidth;
            h = cvs.clientHeight;
            cvs.width = w * dpr;
            cvs.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            nodes = Array.from({ length: NODE_COUNT }).map(() => ({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * 0.20,
                vy: (Math.random() - 0.5) * 0.20,
                r: 1.4 + Math.random() * 1.6,
                phase: Math.random() * Math.PI * 2,
            }));
        };

        const tick = (t) => {
            ctx.clearRect(0, 0, w, h);
            // Move + draw nodes
            for (const n of nodes) {
                if (!reduced) {
                    n.x += n.vx;
                    n.y += n.vy;
                    if (n.x < 0 || n.x > w) n.vx *= -1;
                    if (n.y < 0 || n.y > h) n.vy *= -1;
                }
                const pulse = 0.5 + 0.5 * Math.sin(t * 0.0014 + n.phase);
                ctx.beginPath();
                ctx.fillStyle = palette.node;
                ctx.globalAlpha = 0.45 + 0.55 * pulse;
                ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
                ctx.fill();
                ctx.globalAlpha = 1;
            }
            // Draw connection lines for nearby pairs
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const a = nodes[i], b = nodes[j];
                    const dx = a.x - b.x, dy = a.y - b.y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 < 140 * 140) {
                        const opacity = 1 - d2 / (140 * 140);
                        ctx.strokeStyle = palette.line;
                        ctx.globalAlpha = opacity * 0.7;
                        ctx.lineWidth = 0.6;
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.stroke();
                    }
                }
            }
            ctx.globalAlpha = 1;
            raf = requestAnimationFrame(tick);
        };

        resize();
        const onResize = () => resize();
        window.addEventListener("resize", onResize);
        raf = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
        };
    }, [variant, density]);

    const gridStroke = variant === "light" || variant === "subtle"
        ? "rgba(15, 23, 42, 0.06)"
        : "rgba(103, 232, 249, 0.08)";

    return (
        <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
            {showGrid && (
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                    <defs>
                        <pattern id="tr-techgrid" width="48" height="48" patternUnits="userSpaceOnUse">
                            <path d="M 48 0 L 0 0 0 48" fill="none" stroke={gridStroke} strokeWidth="1" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#tr-techgrid)" />
                </svg>
            )}
            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
            {showScan && (
                <>
                    <div className="tr-scanline tr-scanline-1" />
                    <div className="tr-scanline tr-scanline-2" />
                    <div className="tr-scanline tr-scanline-3" />
                </>
            )}
            <div className="absolute top-6 left-6 font-mono-tr text-[10px] tracking-[0.22em] uppercase opacity-25 select-none drift-1"
                style={{ color: variant === "light" || variant === "subtle" ? "#0e7490" : "#67e8f9" }}>
                · LAB · 01000101 · µ ← 22 · λ 405 nm
            </div>
            <div className="absolute bottom-7 right-7 font-mono-tr text-[10px] tracking-[0.22em] uppercase opacity-20 select-none drift-2 text-right"
                style={{ color: variant === "light" || variant === "subtle" ? "#0e7490" : "#67e8f9" }}>
                N·Ce-H₃ · QAR · 10y · 0xTR
            </div>
        </div>
    );
}
